"use client";

import { useState, useEffect } from "react";
import Icon from "../../../components/ui/AppIcon";

interface Announcement {
  id: string;
  title: string;
  description: string;
  date: string;
  priority: "high" | "medium" | "low";
  category: "weather" | "government" | "utility" | "event";
}

interface AnnouncementCardProps {
  announcement: Announcement;
}

const AnnouncementCard = ({ announcement }: AnnouncementCardProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const getPriorityColor = (priority: string) => {
    const colors = {
      high: "border-destructive bg-destructive/5",
      medium: "border-warning bg-warning/5",
      low: "border-trust bg-trust/5",
    };
    return colors[priority as keyof typeof colors] || "border-border bg-muted";
  };

  const getCategoryIcon = (category: string) => {
    const icons = {
      weather: "CloudIcon",
      government: "OfficeBuildingIcon",
      utility: "LightningBoltIcon",
      event: "CalendarIcon",
    };
    return icons[category as keyof typeof icons] || "InformationCircleIcon";
  };

  const formatDate = (dateString: string) => {
    if (!isHydrated) {
      return dateString;
    }
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div data-component-id="src/app/city-info/components/AnnouncementCard.tsx:58:4" data-component-path="src/app/city-info/components/AnnouncementCard.tsx" data-component-line="58" data-component-end-line="97" data-component-file="AnnouncementCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      className={`rounded-lg border-2 ${getPriorityColor(announcement.priority)} p-4 transition-all duration-300 hover:shadow-md`}
    >
      <div data-component-id="src/app/city-info/components/AnnouncementCard.tsx:61:6" data-component-path="src/app/city-info/components/AnnouncementCard.tsx" data-component-line="61" data-component-end-line="96" data-component-file="AnnouncementCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20space-x-3%22%7D" className="flex items-start space-x-3">
        <div data-component-id="src/app/city-info/components/AnnouncementCard.tsx:62:8" data-component-path="src/app/city-info/components/AnnouncementCard.tsx" data-component-line="62" data-component-end-line="69" data-component-file="AnnouncementCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-10%20h-10%20bg-gradient-to-br%20from-brand-primary%20to-brand-secondary%20rounded-lg%20flex%20items-center%20justify-center%20flex-shrink-0%22%7D" className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon data-component-id="src/app/city-info/components/AnnouncementCard.tsx:63:10" data-component-path="src/app/city-info/components/AnnouncementCard.tsx" data-component-line="63" data-component-end-line="68" data-component-file="AnnouncementCard.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-white%22%7D"
            name={getCategoryIcon(announcement.category) as any}
            size={20}
            variant="solid"
            className="text-white"
          />
        </div>
        <div data-component-id="src/app/city-info/components/AnnouncementCard.tsx:70:8" data-component-path="src/app/city-info/components/AnnouncementCard.tsx" data-component-line="70" data-component-end-line="95" data-component-file="AnnouncementCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20min-w-0%22%7D" className="flex-1 min-w-0">
          <div data-component-id="src/app/city-info/components/AnnouncementCard.tsx:71:10" data-component-path="src/app/city-info/components/AnnouncementCard.tsx" data-component-line="71" data-component-end-line="80" data-component-file="AnnouncementCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20justify-between%20mb-2%22%7D" className="flex items-start justify-between mb-2">
            <h3 data-component-id="src/app/city-info/components/AnnouncementCard.tsx:72:12" data-component-path="src/app/city-info/components/AnnouncementCard.tsx" data-component-line="72" data-component-end-line="74" data-component-file="AnnouncementCard.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-headline%20font-bold%20text-foreground%22%7D" className="font-headline font-bold text-foreground">
              {announcement.title}
            </h3>
            {announcement.priority === "high" && (
              <span data-component-id="src/app/city-info/components/AnnouncementCard.tsx:76:14" data-component-path="src/app/city-info/components/AnnouncementCard.tsx" data-component-line="76" data-component-end-line="78" data-component-file="AnnouncementCard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22bg-destructive%20text-white%20px-2%20py-1%20rounded-full%20font-body%20text-xs%20font-semibold%20flex-shrink-0%20ml-2%22%2C%22textContent%22%3A%22Urgent%22%7D" className="bg-destructive text-white px-2 py-1 rounded-full font-body text-xs font-semibold flex-shrink-0 ml-2">
                Urgent
              </span>
            )}
          </div>
          <p data-component-id="src/app/city-info/components/AnnouncementCard.tsx:81:10" data-component-path="src/app/city-info/components/AnnouncementCard.tsx" data-component-line="81" data-component-end-line="83" data-component-file="AnnouncementCard.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-sm%20text-muted-foreground%20mb-2%20leading-relaxed%22%7D" className="font-body text-sm text-muted-foreground mb-2 leading-relaxed">
            {announcement.description}
          </p>
          <div data-component-id="src/app/city-info/components/AnnouncementCard.tsx:84:10" data-component-path="src/app/city-info/components/AnnouncementCard.tsx" data-component-line="84" data-component-end-line="94" data-component-file="AnnouncementCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%22%7D" className="flex items-center space-x-2">
            <Icon data-component-id="src/app/city-info/components/AnnouncementCard.tsx:85:12" data-component-path="src/app/city-info/components/AnnouncementCard.tsx" data-component-line="85" data-component-end-line="90" data-component-file="AnnouncementCard.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CalendarIcon%22%2C%22className%22%3A%22text-muted-foreground%22%7D"
              name="CalendarIcon"
              size={14}
              variant="outline"
              className="text-muted-foreground"
            />
            <span data-component-id="src/app/city-info/components/AnnouncementCard.tsx:91:12" data-component-path="src/app/city-info/components/AnnouncementCard.tsx" data-component-line="91" data-component-end-line="93" data-component-file="AnnouncementCard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-body%20text-xs%20text-muted-foreground%22%7D" className="font-body text-xs text-muted-foreground">
              {formatDate(announcement.date)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementCard;
